import classNames from 'classnames';
import {isMobile} from 'is-mobile';
import {isEmpty, isNil} from 'lodash';
import {PureComponent, createRef} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';

import {Button, SessionActionControl, Icon, Dropdown, DropdownItem, VideoOverlayContent} from 'components';
import {FacingMode, VideoPinParticipant} from 'enums';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {pictureInPictureStore} from 'services';
import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.IN_SESSION_VIEW.CONTROLS;

type ActionToggleStatus = {
    isHidden?: boolean
};

type State = {
    readonly cameraSwitcherAvailable: boolean;
    readonly devicesAnchorEl: HTMLElement;
    readonly isMobile: boolean;
    readonly pictureInPictureActive: boolean;
}

const initialState: State = {
    cameraSwitcherAvailable: false,
    devicesAnchorEl: null,
    isMobile: false,
    pictureInPictureActive: false
};

type Props = {
    isActive: boolean;
    hasAudioVideoActivity: boolean;
    facingMode: string;
    localNetworkQualityLevel: number;
    remoteNetworkQualityLevel: number;
    videoPinEnabled: boolean;
    videoPinParticipant: VideoPinParticipant;
    pinnedParticipantId?: string;
    isFullScreen: boolean;
    toggleFullScreen: (value: boolean) => void;
    isAudioEnabled: boolean;
    toggleAudio: (value: boolean) => void;
    isVideoEnabled: boolean;
    toggleVideo: (value: boolean) => void;
    onLeaveSession: () => void;
    selectedVideoDeviceId: string | null;
    videoDevices: {deviceId: string, label: string}[];
    audioVideoAsColumn: boolean;
    onVideoPinParticipantUpdate: (participant: VideoPinParticipant, participantId?: string) => void;
    updateVideoDevice: (params: MediaTrackConstraintSet) => void;
    onTogglePictureInPicture: () => void;
    children: (props: any) => React.ReactNode;
} & WithTranslation;

class InSessionClientControls extends PureComponent<Props, State> {
    readonly state = initialState;
    componentRef = createRef<HTMLDivElement>();

    get cameraSwitcherAvailable(): boolean {
        return this.props.videoDevices.length > 1;
    }

    componentDidMount() {
        this.setState({isMobile: isMobile({tablet: true})});
        pictureInPictureStore.subscribe(({active}) => {
            this.setState({pictureInPictureActive: active});
        });
    }

    componentDidUpdate(prevProps: Props) {
        if (isEmpty(prevProps.videoDevices) && !isEmpty(this.props.videoDevices)) {
            if (this.props.videoDevices.length === 1) {
                this.updateVideoDeviceById(this.props.videoDevices[0].deviceId);
            }
        }
    }

    getClientRectSize = (rect: { width: number, height: number }): { rectWidth: number, rectHeight: number } =>
        ({rectWidth: rect.width, rectHeight: rect.height});

    handleOpenDevicesClose = () => {
        this.setState({devicesAnchorEl: null});
    };

    toggleAudio = () => {
        this.props.toggleAudio(!this.props.isAudioEnabled);
    };

    toggleVideo = () => {
        this.props.toggleVideo(!this.props.isVideoEnabled);
    };

    toggleFullScreen = () => {
        this.props.toggleFullScreen(!this.props.isFullScreen);
    };

    updateVideoDeviceById = (deviceId: string) => {
        if (deviceId !== '') {
            this.props.updateVideoDevice({deviceId: {exact: deviceId}});
        }
    };

    updateVideoDeviceByFacingMode = (facingMode: string) => {
        if (!isNil(facingMode)) {
            this.props.updateVideoDevice({facingMode: {exact: facingMode}});
        }
    };

    handleOpenDevicesClick = (event) => {
        // Switch to next device if there more than 1 device and on mobile
        if (this.props.videoDevices.length >= 2 && this.state.isMobile) {
            if (this.props.facingMode === FacingMode.Environment) {
                this.updateVideoDeviceByFacingMode(FacingMode.User);
            } else {
                this.updateVideoDeviceByFacingMode(FacingMode.Environment);
            }
            return;
        }

        this.setState({devicesAnchorEl: event.currentTarget});
    };

    render() {
        const {
            handleOpenDevicesClick,
            handleOpenDevicesClose,
            updateVideoDeviceById,
            toggleFullScreen,
            toggleAudio,
            toggleVideo,
            cameraSwitcherAvailable
        } = this;

        const {devicesAnchorEl, pictureInPictureActive} = this.state;

        const {
            localNetworkQualityLevel,
            remoteNetworkQualityLevel,
            isFullScreen,
            hasAudioVideoActivity,
            isAudioEnabled,
            isVideoEnabled,
            videoPinEnabled,
            videoPinParticipant,
            isActive,
            selectedVideoDeviceId,
            videoDevices,
            onLeaveSession,
            onTogglePictureInPicture,
            onVideoPinParticipantUpdate,
            t
        } = this.props;

        const devicesListOpen = Boolean(devicesAnchorEl);

        const renderOverLocalVideoContent = () => (
            <VideoOverlayContent
                participant={VideoPinParticipant.Local}
                pinnedParticipant={videoPinParticipant}
                networkQualityLevel={localNetworkQualityLevel}
                videoPinEnabled={videoPinEnabled}
                onVideoPinParticipantUpdate={onVideoPinParticipantUpdate}
            />
        );

        const renderOverRemoteVideoContent = () => (
            <VideoOverlayContent
                participant={VideoPinParticipant.Remote}
                pinnedParticipant={videoPinParticipant}
                networkQualityLevel={remoteNetworkQualityLevel}
                videoPinEnabled={videoPinEnabled}
                onVideoPinParticipantUpdate={onVideoPinParticipantUpdate}
            />
        );

        const renderOverParticipantContent = (participantId: string) => (
            <VideoOverlayContent
                participantId={participantId}
                pinnedParticipantId={this.props.pinnedParticipantId}
                videoPinEnabled={videoPinEnabled && !this.props.audioVideoAsColumn}
                onVideoPinParticipantUpdate={this.props.onVideoPinParticipantUpdate}
            />
        );

        const renderToggleFullScreen = () => (
            <div
                className={classNames('vs-fullscreen-icon', {
                    active: isActive,
                    // Workaround: incorrect view with no pinned participant on mobile
                    'tw-hidden': !videoPinParticipant,
                })}
                onClick={toggleFullScreen}
            >
                <Icon color='light' name={isFullScreen ? 'shrink' : 'expand'} size='lg' />
            </div>
        );
        const renderPictureInPicture = () => (
            pictureInPictureStore.getValue().supported &&
                <div
                    className={classNames('vs-picture-in-picture-icon', {
                        active: isActive
                    })}
                    onClick={onTogglePictureInPicture}
                >
                    <Icon
                        color='light'
                        name={pictureInPictureActive ? 'multiscreen-off' : 'multiscreen-on'}
                        size='lg'
                    />
                </div>
        );
        const renderToggleAudio = ({isHidden}: ActionToggleStatus = {}) => (
            <div className='vs-audio-icon tw-min-w-12 tw-min-h-16'>
                {!isHidden &&
                    <SessionActionControl
                        label={t(T_KEYS.MUTE)}
                        isDisabled={!isAudioEnabled}
                        onClick={toggleAudio}
                    >
                        <Icon name='microphone' size='4xl' />
                    </SessionActionControl>}
            </div>
        );
        const renderToggleVideo = ({isHidden}: ActionToggleStatus = {}) => (
            <div className='vs-video-icon tw-min-w-12 tw-min-h-16'>
                {!isHidden &&
                    <SessionActionControl
                        isDisabled={!isVideoEnabled}
                        label={t(T_KEYS.VIDEO)}
                        onClick={toggleVideo}
                    >
                        <Icon name='camera' size='4xl' />
                    </SessionActionControl>}
            </div>
        );
        const renderShareScreen = () => (
            <div className='vs-share-screen-icon'>
                <SessionActionControl
                    label={t(T_KEYS.SHARE_SCREEN)}
                    onClick={toggleFullScreen}
                >
                    <Icon name='camera' size='4xl' />
                </SessionActionControl>
            </div>
        );
        const renderSwitch = () => (
            <div className='vs-switch-icon'>
                <SessionActionControl
                    label={t(T_KEYS.SWITCH)}
                    onClick={(event) => handleOpenDevicesClick(event)}
                >
                    <Icon name='switch-camera' size='2xl' color='light' />
                </SessionActionControl>
            </div>
        );

        const renderLeaveSession = () => (
            <div className={classNames('vs-end-call-icon', {
                wide: hasAudioVideoActivity
            })}
            >
                <Button
                    color='attention'
                    width='auto'
                    height='auto'
                    rounded
                    onClick={onLeaveSession}
                >
                    <Icon name='phone-end' size='4xl' />
                </Button>
                <div className='icon-text'>{t(T_KEYS.LEAVE)}</div>
            </div>
        );
        // TODO: replace with CameraSwitcher component
        const renderChangeDevice = () => {
            if (cameraSwitcherAvailable && hasAudioVideoActivity && isVideoEnabled && videoDevices?.length >= 2) {
                if (this.state.isMobile) {
                    return renderSwitch();
                }
                return (
                    <Dropdown
                        open={devicesListOpen}
                        triggerNode={renderSwitch()}
                        onClose={handleOpenDevicesClose}
                    >
                        {videoDevices.map((option) => (
                            <DropdownItem
                                key={option.deviceId}
                                selected={option.deviceId === selectedVideoDeviceId}
                                onClick={() => {
                                    handleOpenDevicesClose();
                                    updateVideoDeviceById(option.deviceId);
                                }}
                            >
                                {option.label}
                            </DropdownItem>
                        ))}
                    </Dropdown>
                );
            }
            return null;
        };

        return (
            this.props.children({
                renderOverLocalVideoContent,
                renderOverRemoteVideoContent,
                renderOverParticipantContent,
                renderToggleFullScreen,
                renderToggleAudio,
                renderToggleVideo,
                renderLeaveSession,
                renderChangeDevice,
                renderShareScreen,
                renderPictureInPicture
            })
        );
    }
}

const wrappedComponent = withTranslation()(InSessionClientControls);
export {wrappedComponent as InSessionClientControls};
